<template>
  <div style="padding:24px;">
    <div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:20px;">
      <div>
        <h2 style="font-size:20px;font-weight:600;margin:0 0 4px 0;">投资者适当性问卷（通用模版）</h2>
        <p style="font-size:13px;color:#909399;margin:0;">用于开户流程中的投资者适当性评估，包含基本信息、投资经验、风险承受能力及产品认知四个部分。</p>
      </div>
      <div>
        <el-button v-auth="'business:questionConfig:edit'" v-if="!editing" type="primary" @click="editing=true">编辑</el-button>
        <el-button v-auth="'business:questionConfig:edit'" v-else @click="editing=false">完成编辑</el-button>
      </div>
    </div>

    <div style="display:flex;gap:12px;margin-bottom:20px;">
      <div class="stat-card"><div class="stat-num">{{ survey.totalQuestions }}</div><div class="stat-label">题目总数</div></div>
      <div class="stat-card"><div class="stat-num">{{ survey.totalSections }}</div><div class="stat-label">问卷部分</div></div>
      <div class="stat-card"><div class="stat-num">{{ survey.totalKeyQuestions }}</div><div class="stat-label">关键题</div></div>
      <div class="stat-card">
        <div class="stat-num">v1.0</div><div class="stat-label">当前版本</div>
      </div>
    </div>

    <div v-loading="loading">
      <div v-for="(sec, si) in survey.sections" :key="sec.id" class="survey-section">
        <div class="section-header">
          <span class="section-icon">{{ si+1 }}</span>
          <span class="section-title">{{ sec.sectionName }}</span>
          <span style="flex:1;"></span>
          <span class="section-tag">{{ sec.scoringEnabled===1?'计分':'不计分 · 合规留档' }}{{ sec.scoringEnabled===1&&sec.weightCoefficient?' · 权重'+sec.weightCoefficient:'' }}</span>
          <div class="section-actions" v-if="editing">
            <el-button v-auth="'business:questionConfig:edit'" size="small" @click="">计分配置</el-button>
            <el-dropdown trigger="click" @command="handleSectionCmd(si,$event)">
              <el-button v-auth="'business:questionConfig:edit'" size="small">⋮更多</el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item v-auth="'business:questionConfig:edit'" command="up">上移</el-dropdown-item>
                <el-dropdown-item v-auth="'business:questionConfig:edit'" command="down">下移</el-dropdown-item>
                <el-dropdown-item v-auth="'business:questionConfig:edit'" command="rename">重命名Section</el-dropdown-item>
                <el-dropdown-item v-auth="'business:questionConfig:edit'" command="delete" style="color:#f56c6c;">删除Section</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>

        <div v-for="q in sec.questions" :key="q.id" class="q-card">
          <div class="q-header">
            <span class="q-num">Q{{ qNum(q) }}</span>
            <span class="q-text">{{ q.questionText }}</span>
            <span class="q-type" :class="{'q-type-key':q.isKey===1}">{{ typeLabel(q.questionType) }}{{ q.isKey===1?' · 关键题':'' }}</span>
            <div class="q-actions" v-if="editing">
              <button v-auth="'business:questionConfig:edit'" class="q-btn" title="编辑">✏</button>
              <button v-auth="'business:questionConfig:edit'" class="q-btn" title="复制">⧉</button>
              <button v-auth="'business:questionConfig:edit'" class="q-btn danger" title="删除">🗑</button>
            </div>
          </div>
          <div class="q-options" v-if="q.optionsJson">
            <!-- 关键题/否决题：result 样式 -->
            <template v-if="sec.scoringEnabled===2 && q.isKey===1">
              <div v-for="(opt, oi) in parseOptions(q.optionsJson)" :key="oi" class="score-row" :class="{'score-reject':opt.result==='否决'||opt.result==='阻断'}">
                <span>{{ opt.text }}</span><span class="score-val" :class="{'score-fail':opt.result==='否决'||opt.result==='阻断','score-warn':opt.result==='转人工','score-pass':opt.result==='通过'}">{{ opt.result }}</span>
              </div>
            </template>
            <!-- 计分题：行展示，分数在右 -->
            <template v-else-if="sec.scoringEnabled===1">
              <div v-for="(opt, oi) in parseOptions(q.optionsJson)" :key="oi" class="score-row">
                <span>{{ opt.text }}</span><span class="score-val">{{ opt.score || 0 }}分</span>
              </div>
            </template>
            <!-- 不计分题：标签展示 -->
            <template v-else>
              <span v-for="(opt, oi) in parseOptions(q.optionsJson)" :key="oi" class="option-tag" :class="{'option-warn':opt.text.includes('贷款')}">{{ opt.text }}</span>
            </template>
          </div>
          <div v-if="q.alertText" class="q-alert">
            <span class="alert-icon">⚠</span>
            <span>{{ q.alertText }}</span>
          </div>
        </div>

        <div class="add-q" v-if="editing">
          <el-button v-auth="'business:questionConfig:edit'" type="text" style="color:var(--brand-primary);">+ 添加题目到本部分</el-button>
        </div>
      </div>
    </div>

    <div style="margin-top:16px;" v-if="editing">
      <el-button v-auth="'business:questionConfig:edit'" type="primary" icon="el-icon-plus">+ 添加新的问卷部分</el-button>
    </div>

    <!-- 评分规则与结果影响（固定内容） -->
    <div class="survey-section">
      <div class="section-header">
        <span class="section-icon">i</span>
        <span class="section-title">评分规则与结果影响</span>
      </div>
      <div style="padding:14px 20px;">
        <table class="score-table">
          <thead><tr><th>总分区间</th><th>风险等级</th><th>结果影响</th></tr></thead>
          <tbody>
            <tr v-for="(r, ri) in survey.scoreRules" :key="ri">
              <td>{{ r.range }}</td>
              <td><span class="risk-badge" :class="riskClass(r.level)">{{ r.level }}</span></td>
              <td>{{ r.effect }}</td>
            </tr>
          </tbody>
        </table>
        <div class="veto-section">
          <strong>关键题否决规则：</strong>Q12/Q13 为强制否决题，任一选"否"即阻断开户流程；Q14/Q15 选"是"不直接否决，但触发合规审核流程。
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getSurvey } from '@/api/questionConfig'

let qNumMap = {}
export default {
  name: 'QuestionConfigList',
  data() {
    return { loading: false, editing: false, survey: { sections: [], totalQuestions: 0, totalSections: 0, totalKeyQuestions: 0, scoreRules: [] } }
  },
  mounted() { this.fetchSurvey() },
  methods: {
    fetchSurvey() {
      this.loading = true
      getSurvey().then(res => {
        if (res.code === 0 && res.result) {
          this.survey = res.result
          // 计算每个题目的全局编号
          let n = 1
          qNumMap = {}
          this.survey.sections.forEach(sec => sec.questions.forEach(q => { qNumMap[q.id] = n++ }))
        }
      }).finally(() => { this.loading = false })
    },
    qNum(q) { return qNumMap[q.id] || q.sort },
    typeLabel(t) { return t==='single'?'单选':t==='multi'?'多选':t==='dropdown'?'下拉选择':t },
    parseOptions(json) { try { return JSON.parse(json) } catch(e) { return [] } },
    riskClass(level) { return level==='低风险承受'?'risk-low':level==='中等风险承受'?'risk-mid':'risk-high' },
    handleSectionCmd(si, cmd) { this.$message.info(cmd + ' section ' + si) }
  }
}
</script>

<style scoped>
.stat-card { flex:1; background:#fff; border:1px solid #e4e7ed; border-radius:8px; padding:16px; text-align:center; }
.stat-num { font-size:24px; font-weight:700; color:#303133; }
.stat-label { font-size:12px; color:#909399; margin-top:4px; }
.version-actions { margin-top:6px; font-size:11px; }
.version-actions a { color:var(--brand-primary); margin:0 4px; text-decoration:none; }
.version-actions a:hover { text-decoration:underline; }
.survey-section { background:#fff; border:1px solid #e4e7ed; border-radius:8px; margin-bottom:16px; overflow:hidden; }
.section-header { display:flex; align-items:center; gap:10px; padding:14px 20px; background:#fafafa; border-bottom:1px solid #ebeef5; }
.section-icon { width:28px; height:28px; border-radius:50%; background:var(--brand-primary); color:#fff; display:flex; align-items:center; justify-content:center; font-size:14px; font-weight:600; flex-shrink:0; }
.section-title { font-size:15px; font-weight:600; color:#303133; }
.section-tag { font-size:12px; color:#909399; }
.section-actions { margin-left:auto; display:flex; gap:6px; }
.q-card { padding:14px 20px; border-bottom:1px solid #f0f0f0; }
.q-card:last-child { border-bottom:none; }
.q-header { display:flex; align-items:center; gap:10px; }
.q-num { font-size:13px; font-weight:600; color:var(--brand-primary); min-width:28px; }
.q-text { font-size:14px; color:#303133; flex:1; }
.q-type { font-size:11px; color:#909399; background:#f0f2f5; border-radius:3px; padding:1px 8px; white-space:nowrap; }
.q-type-key { background:#fef0f0; color:#f56c6c; }
.q-actions { display:flex; gap:2px; }
.q-btn { background:none; border:none; cursor:pointer; font-size:14px; padding:2px 4px; border-radius:3px; }
.q-btn:hover { background:#f0f2f5; }
.q-btn.danger:hover { background:#fef0f0; }
.q-options { margin-top:10px; margin-left:38px; }
.score-row { display:flex; justify-content:space-between; padding:4px 0; font-size:13px; color:#606266; border-bottom:1px dashed #f0f0f0; }
.score-val { color:var(--brand-primary); font-weight:500; margin-left:auto; min-width:50px; text-align:right; }
.score-row:last-child { border-bottom:none; }
.score-pass { color:#67c23a; }
.score-fail { color:#f56c6c; }
.score-warn { color:#e6a23c; }
.score-reject { background:#fef0f0; }
.option-tag { display:inline-block; background:#f0f2f5; border-radius:3px; padding:2px 10px; font-size:12px; color:#606266; margin:2px 6px 2px 0; }
.option-warn { background:#fef0f0; color:#f56c6c; }
.q-alert { margin-top:8px; margin-left:38px; padding:8px 12px; background:#fff7e6; border-radius:4px; font-size:12px; color:#e6a23c; }
.alert-icon { margin-right:4px; }
.add-q { padding:10px 20px; border-top:1px solid #f0f0f0; }
.score-table { width:100%; border-collapse:collapse; font-size:13px; }
.score-table th { background:#f5f7fa; padding:10px; text-align:left; font-weight:600; color:#303133; border-bottom:2px solid #e4e7ed; }
.score-table td { padding:10px; color:#606266; border-bottom:1px solid #ebeef5; }
.veto-section { margin-top:14px; padding:10px 14px; background:#fef0f0; border-radius:4px; font-size:13px; color:#f56c6c; line-height:1.8; }
.risk-badge { font-size:12px; border-radius:3px; padding:1px 8px; font-weight:500; }
.risk-low { background:#e1f3d8; color:#67c23a; }
.risk-mid { background:#faecd8; color:#e6a23c; }
.risk-high { background:#fef0f0; color:#f56c6c; }
</style>
